import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Declare global window.ENV_CONFIG for runtime environment variables
declare global {
  interface Window {
    ENV_CONFIG?: {
      REACT_APP_API_URL?: string;
    };
  }
}

interface User {
  id: string;
  username: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
        } catch (error) {
          console.error('Failed to parse saved user data:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      console.log('AuthContext: Login attempt with:', { username, password });
      
      // Get API URL from runtime config or fallback to build-time env var
      const API_BASE_URL = (window.ENV_CONFIG && window.ENV_CONFIG.REACT_APP_API_URL) || 
                           process.env.REACT_APP_API_URL || 
                           'http://localhost:3001/api';
      
      console.log('AuthContext: Using API URL:', API_BASE_URL);
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.log('AuthContext: Login failed:', errorData.message);
        return false;
      }

      const data = await response.json();
      console.log('AuthContext: Login successful, received data:', data);

      const userData: User = {
        id: data.data.user.id.toString(),
        username: data.data.user.username,
        role: data.data.user.role
      };
      
      // Save to localStorage
      localStorage.setItem('authToken', data.data.token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      console.log('AuthContext: Saved to localStorage, setting user state');
      setUser(userData);
      
      console.log('AuthContext: Login successful, returning true');
      return true;
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
